import { useDraggable } from '@dnd-kit/core'
import { ArrowLeft, ArrowRight, CalendarDays, CheckSquare, ExternalLink, MessageSquare, MoreHorizontal, Paperclip, Pencil, Store, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import { isOverdue } from '../../taskFilters'
import { TASK_STATUSES, type Task, type TaskStatus } from '../../types'
import { PriorityBadge, TaskTypeBadge } from '../StatusBadges'

type Props = {
  task: Task
  projectId: string
  customer?: string
  attachments: number
  assignee: string | null
  canWrite: boolean
  onEdit: (t: Task) => void
  onDelete: (t: Task) => void
  onMove: (t: Task, status: TaskStatus) => void
}

const shortName = (name: string) => {
  const [first = '', last = ''] = name.split(' ')
  return last ? `${first} ${last[0]}.` : first
}

export function TaskCard({ task, projectId, customer, attachments, assignee, canWrite, onEdit, onDelete, onMove }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id, disabled: !canWrite })
  const idx = TASK_STATUSES.indexOf(task.status)
  const prev = TASK_STATUSES[idx - 1]
  const next = TASK_STATUSES[idx + 1]
  const done = task.checklist.filter((c) => c.done).length
  const open = () => navigate(`/projects/${projectId}/tasks/${task.id}`)

  return (
    <article
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cn(
        'group relative rounded-xl border bg-card p-3 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card-hover motion-reduce:hover:translate-y-0',
        canWrite && 'cursor-grab touch-none active:cursor-grabbing',
        isDragging && 'z-50 rotate-1 opacity-80 shadow-lg',
      )}
      {...attributes}
      {...listeners}
      aria-roledescription={t('projects.kanban.draggable')}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] text-muted-foreground">{task.key}</span>
        <TaskTypeBadge type={task.type} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="ml-auto size-7 text-muted-foreground" aria-label={t('projects.rowActions', { name: task.key })} onPointerDown={(e) => e.stopPropagation()}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            <DropdownMenuItem onSelect={open}>
              <ExternalLink />
              {t('projects.kanban.open')}
            </DropdownMenuItem>
            {canWrite ? (
              <>
                <DropdownMenuItem onSelect={() => onEdit(task)}>
                  <Pencil />
                  {t('common.edit')}
                </DropdownMenuItem>
                {prev ? (
                  <DropdownMenuItem onSelect={() => onMove(task, prev)}>
                    <ArrowLeft />
                    {t('projects.kanban.moveTo', { status: t(`projects.taskStatus.${prev}`) })}
                  </DropdownMenuItem>
                ) : null}
                {next ? (
                  <DropdownMenuItem onSelect={() => onMove(task, next)}>
                    <ArrowRight />
                    {t('projects.kanban.moveTo', { status: t(`projects.taskStatus.${next}`) })}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onDelete(task)} className="text-destructive [&_svg]:text-destructive">
                  <Trash2 />
                  {t('common.delete')}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <button type="button" onClick={open} onPointerDown={(e) => e.stopPropagation()} className="mt-1.5 block w-full rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <h4 className="text-sm font-semibold leading-snug">{task.title}</h4>
        {task.description ? <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{task.description}</p> : null}
      </button>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {customer ? (
          <span className="inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground">
            <Store className="size-3 shrink-0" aria-hidden />
            <span className="truncate">{customer}</span>
          </span>
        ) : null}
        <PriorityBadge priority={task.priority} compact />
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5">
          {assignee ? <Avatar name={assignee} className="size-6 [&>span]:text-[9px]" /> : null}
          <span className="truncate">{assignee ? shortName(assignee) : t('projects.work.unassigned')}</span>
        </span>
        <span className={cn('inline-flex shrink-0 items-center gap-1 tabular-nums', isOverdue(task) && 'font-medium text-destructive')}>
          <CalendarDays className="size-3.5" aria-hidden />
          {fmtDate(task.dueDate)}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-3 border-t pt-2 text-[11px] text-muted-foreground">
        <span className={cn('inline-flex items-center gap-1 tabular-nums', task.checklist.length > 0 && done === task.checklist.length && 'text-success')}>
          <CheckSquare className="size-3.5" aria-hidden />
          {done}/{task.checklist.length}
        </span>
        <span className="inline-flex items-center gap-1 tabular-nums" title={t('projects.taskDetail.comments')}>
          <MessageSquare className="size-3.5" aria-hidden />
          {task.comments.length}
        </span>
        <span className="inline-flex items-center gap-1 tabular-nums" title={t('projects.taskDetail.attachments')}>
          <Paperclip className="size-3.5" aria-hidden />
          {attachments}
        </span>
      </div>
    </article>
  )
}
